/**
 * @fileoverview
 * Register widget for programmatic control by `_fwn.widgets`.
 * All widgets that use WidgetWrapper will automatically be registered.
 */

import { useEffect } from 'react'

import {
  registerFWNWidget,
  unregisterFWNWidget,
} from '@assets/fwnInit/fwnWidgets'
import { logInfoCollapsed } from '@core/logging/helpers'
import type { WidgetTypesEnum } from '@core/types'
import type {
  WidgetInterruptCallbackFn,
  WidgetInterruptEvent,
} from '@core/widgets/interrupts/types'
import { useStoreApi } from '@core/widgets/store'

export interface IProps {
  widgetId: string
  name: string
  type: WidgetTypesEnum
}

/**
 * Register widget for programmatic control by `_fwn.widgets`.
 */
export function useRegisterFwnWidget(props: IProps) {
  const { widgetId, name, type } = props
  const storeApi = useStoreApi()

  useEffect(() => {
    if (widgetId && type) {
      registerFWNWidget(widgetId, name ?? '', type, {
        widgetId,
        name: name ?? '',
        type,
        registerCallback: (
          event: WidgetInterruptEvent,
          fn: WidgetInterruptCallbackFn,
        ) => {
          logInfoCollapsed(`InterruptionCallback: ${name}/${event}`, {
            widget: widgetId,
            name,
            event,
            fn,
          })
          storeApi.getState().actions.registerInterruptionCallback(event, fn)
        },
        unregisterCallback: (event: WidgetInterruptEvent) => {
          storeApi.getState().actions.unregisterInterruptionCallback(event)
        },
      })
    }
  }, [widgetId, name, type, storeApi])

  useEffect(
    () => () => {
      unregisterFWNWidget(widgetId ?? '', name ?? '')
    },
    [widgetId, name],
  )
}
